import { HTTPError } from 'ky'

import { getTaskMarkers } from '@/feature/observatory/components/TaskMapOverlay/getTaskMarkers'

import { TaskDefinition } from '../../types/task'
import { TaskSchedule } from '../../utils/taskSchedule'

// Roughly the centre of South Africa. Only ever seen for a task with no locatable stops, since
// TaskMapOverlay fits the route bounds on mount and immediately supersedes this.
export const FALLBACK_MAP_CENTER: google.maps.LatLngLiteral = {
  lat: -30.069547984853443,
  lng: 24.27277106134315,
}

export const INITIAL_MAP_ZOOM = 6

/**
 * Where to construct the map before the route fit runs. Starting on the task's first stop rather
 * than a national default keeps the initial fit a short adjustment instead of a long fly-in.
 */
export const getInitialMapCenter = (task: TaskDefinition): google.maps.LatLngLiteral => {
  const [firstMarker] = getTaskMarkers(task)
  return firstMarker ? firstMarker.position : FALLBACK_MAP_CENTER
}

/**
 * Mirrors the observatory's map options so the two maps navigate identically — same zoom and
 * fullscreen controls in the same places, same fractional zoom, same suppressed POI clicks.
 *
 * Must stay a stable module-level object: MapRenderer keys its init effect on a structural hash of
 * these options and tears down and rebuilds the whole map when it changes. The ControlPosition
 * lookups are getters so nothing dereferences the Maps API before it has loaded.
 */
export const TASK_FOCUS_MAP_OPTIONS: google.maps.MapOptions = {
  clickableIcons: false,
  mapTypeControl: false,
  mapTypeControlOptions: {
    mapTypeIds: ['roadmap', 'satellite', 'hybrid'],
    get position() {
      return window.google?.maps.ControlPosition?.LEFT_BOTTOM
    },
    get style() {
      return window.google?.maps.MapTypeControlStyle?.HORIZONTAL_BAR
    },
  },
  streetViewControl: false,
  zoomControl: true,
  isFractionalZoomEnabled: true,
  // Pinned rather than left at google's 'auto', which resolves to 'cooperative' on a scrollable
  // page and 'greedy' otherwise. The observatory fills the viewport and never scrolls, so it gets
  // 'greedy' for free; the execution board's task table does scroll, which would otherwise leave
  // this map demanding ctrl+scroll to zoom while the observatory zooms on scroll alone.
  gestureHandling: 'greedy',
}

// A scheduled task's window can sit entirely in the future, where there is no history to draw, so
// fall back to the last day up to now rather than opening replay on an empty range.
const FALLBACK_RANGE_HOURS = 24

/**
 * The span replay covers for a task: the trip's own extent, clamped so it never reaches past now.
 * BRI-1476 asks for the vehicle history "over the extent of the trip", so the history lines up
 * with the route already drawn on the map instead of an arbitrary recent window.
 *
 * Takes the task's schedule (see getTaskSchedule), or null when it has none to go on.
 */
export const getReplayRange = (
  schedule: TaskSchedule | null,
  now: Date = new Date(),
): { rangeStartTime: Date; rangeEndTime: Date } => {
  // Nothing has been recorded yet for a trip that has not started, or has no known start.
  if (!schedule || !(schedule.start.getTime() < now.getTime())) {
    return {
      rangeStartTime: new Date(now.getTime() - FALLBACK_RANGE_HOURS * 60 * 60 * 1000),
      rangeEndTime: now,
    }
  }

  return {
    rangeStartTime: schedule.start,
    rangeEndTime: schedule.end.getTime() > now.getTime() ? now : schedule.end,
  }
}

export type TrailStatus = 'loading' | 'refreshing' | 'not-found' | 'error' | 'empty' | 'ready'

/**
 * Telemetry answers 404 when it holds no trip for the task — never registered, or registered under
 * another fleet. Retrying cannot help, unlike a failed query, so it is told apart from other errors.
 */
export const isNotFoundError = (error: unknown) =>
  error instanceof HTTPError && error.response.status === 404

/** Where the task's trail fetch stands, collapsed to the one state the map needs to show. */
export const getTrailStatus = ({
  isLoading,
  isRefreshing,
  error,
  pointCount,
}: {
  isLoading: boolean
  isRefreshing: boolean
  error: unknown
  pointCount: number
}): TrailStatus => {
  if (error) return isNotFoundError(error) ? 'not-found' : 'error'
  if (isLoading) return 'loading'
  // A refetch at a new resolution keeps the previous trail drawn, so say so without hiding it.
  if (isRefreshing) return 'refreshing'
  if (pointCount === 0) return 'empty'
  return 'ready'
}

/**
 * Every replay message in one place, so they read as one voice: what is happening, and only where
 * it helps, what the user can do about it. Each is keyed by a cause detected from the task or the
 * response — not by today's backend behaviour — so none goes stale if telemetry starts recording
 * more of the trip.
 */
export const REPLAY_MESSAGES = {
  checking: 'Checking for trip history…',
  updating: 'Updating trip history…',
  notStarted: 'Trip history will be available once this trip has started.',
  awaitingFirstStop: 'Trip history will be available once the vehicle reaches its first stop.',
  noHistory: 'No trip history has been recorded for this task.',
  notFound: 'Trip history is not available for this task.',
  loadFailed: 'Trip history could not be loaded. Reopen the task to try again.',
} as const

/**
 * Why an empty trail is empty, from what the task itself knows. The trail is recorded from the
 * vehicle's first stop, so a trip that has not started, or not yet reached a stop, has nothing to
 * replay yet — that is the expected state, not a fault.
 */
export const getEmptyTrailReason = (
  task: TaskDefinition,
  schedule: TaskSchedule,
  now: Date = new Date(),
): string => {
  if (schedule.start.getTime() > now.getTime()) return REPLAY_MESSAGES.notStarted

  const hasReachedAStop = task.taskDefinition.locations.some(
    (location) => location.actualArrival != null,
  )
  return hasReachedAStop ? REPLAY_MESSAGES.noHistory : REPLAY_MESSAGES.awaitingFirstStop
}

/**
 * Why the Replay button can't be pressed yet, or null once there is a trail to play. Answered
 * before replay opens, so the user learns there is nothing to replay from the button's tooltip
 * rather than by opening an empty replay.
 *
 * A task with no usable schedule has no trip to ask telemetry about, whatever the trail fetch says.
 */
export const getReplayUnavailableReason = (
  status: TrailStatus,
  task: TaskDefinition,
  schedule: TaskSchedule | null,
  now: Date = new Date(),
): string | null => {
  if (!schedule) return REPLAY_MESSAGES.notFound

  switch (status) {
    case 'loading':
      return REPLAY_MESSAGES.checking
    case 'empty':
      return getEmptyTrailReason(task, schedule, now)
    case 'not-found':
      return REPLAY_MESSAGES.notFound
    case 'error':
      return REPLAY_MESSAGES.loadFailed
    case 'refreshing':
    case 'ready':
      return null
  }
}

/**
 * What to tell the user over a replay already in progress. Only a refetch at a new resolution can
 * happen by then; an empty or still-loading trail never gets this far, since the button stays shut.
 */
export const getReplayTrailMessage = (status: TrailStatus): string | null => {
  switch (status) {
    case 'refreshing':
      return REPLAY_MESSAGES.updating
    case 'not-found':
      return REPLAY_MESSAGES.notFound
    case 'error':
      return REPLAY_MESSAGES.loadFailed
    default:
      return null
  }
}
